import type {
  ASSISTANT_COLORS,
  AssistantTask,
  ConversationWorkflowRun,
  CrowdexAssistant,
} from '@crowdex/agent-contracts/api';
import { workflowRunRuns } from '@/lib/desktop-shell/sidebar-model';

/**
 * What the project conversation's views say (D65–D67, mobile 2d/2e), from the project, its
 * automations, its personas and its tasks. Pure, so the views stay thin.
 */

/**
 * The agent a saved agent runs on, as the user knows it: its name among the agents listed here,
 * else its id in words ("claude-code" reads "Claude Code").
 */
export function agentLabel(
  agent: string | null | undefined,
  agents: readonly { id: string; name: string }[] = [],
): string {
  if (!agent) return 'Agent';
  const listed = agents.find((entry) => entry.id === agent)?.name;
  if (listed) return listed;
  return agent
    .replace(/-acp$/, '')
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(' ');
}

/** The avatar tones, in order: personas take them as they come. */
const PERSONA_TONES = ['bg-running/60', 'bg-success/45', 'bg-mode-purple/65'] as const;

/** A persona's avatar colour class, stable by its place among the project's personas. */
export function personaTone(index: number): string {
  return PERSONA_TONES[index % PERSONA_TONES.length] ?? 'bg-control';
}

/** The palette's colours by name, for those who do not see them (a swatch's label). */
const PROJECT_COLOR_NAMES: Record<(typeof ASSISTANT_COLORS)[number], string> = {
  '#8b7cf6': 'Violet',
  '#d47fd6': 'Orchid',
  '#4cc27a': 'Green',
  '#7fb4ff': 'Blue',
  '#f07178': 'Coral',
  '#e8835a': 'Orange',
  '#4fc1c9': 'Teal',
};

/** A project colour's name: "Violet"; one stored outside today's palette is a custom one. */
export function assistantColorName(color: string): string {
  return (
    (PROJECT_COLOR_NAMES as Record<string, string | undefined>)[color.toLowerCase()] ??
    'Custom colour'
  );
}

/**
 * "3 agents · 4 automations" (D65 header): the saved agents it hands work to, the automations it
 * runs (left out until they are listed).
 */
export function delegatesSummary(agents: number, automations: number | null): string {
  const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
  return automations === null
    ? plural(agents, 'agent')
    : `${plural(agents, 'agent')} · ${plural(automations, 'automation')}`;
}

/** "Since Oct 2". */
export function placeLine(project: Pick<CrowdexAssistant, 'createdAt'>): string {
  const since = new Date(project.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `Since ${since}`;
}

/** A task's state as its icon draws it; a run without a step yet has not begun. */
export type TaskIconState = 'succeeded' | 'waiting' | 'running' | 'pending' | 'failed' | 'stopped';

export function taskIconState(task: Pick<AssistantTask, 'workflowRun'>): TaskIconState {
  const run = task.workflowRun;
  if (run.runId === 'queued' && run.state === 'waiting') return 'pending';
  // A task an agent does alone has no step: it runs while its conversation's turn does.
  if (run.state === 'running' && run.step === null && run.workflowId !== null) return 'pending';
  return run.state;
}

/** Where a task stands in the plan of a run that plans it (Work as a team), if one does. */
export function plannedState(
  conversationId: string,
  tasks: readonly Pick<AssistantTask, 'workflowRun'>[],
): PlanRow['state'] | null {
  for (const task of tasks)
    for (const item of task.workflowRun.plan?.items ?? [])
      if (item.conversationId === conversationId) return item.state;
  return null;
}

/**
 * The step line of a task: "Develop waits on you", "Review, round 2 of 3", "PR #556 merged";
 * one its plan holds back says so ("next in the plan").
 */
export function taskStepLine(
  task: Pick<AssistantTask, 'workflowRun' | 'pullRequest'>,
  planned: PlanRow['state'] | null = null,
): string {
  const run = task.workflowRun;
  if (run.state === 'succeeded')
    return task.pullRequest === null
      ? 'done'
      : `PR #${task.pullRequest.number} ${task.pullRequest.state}`;
  if (run.state === 'failed') return run.step === null ? 'failed' : `${run.step} failed`;
  if (run.state === 'stopped') return 'stopped';
  if (run.runId === 'queued') return 'waits to start';
  if (run.workflowId === null) return run.state === 'waiting' ? 'waits on you' : 'running';
  if (run.step === null) {
    if (planned === 'next') return 'next in the plan';
    if (planned === 'later') return 'in a later wave';
    return 'starting';
  }
  if (run.state === 'waiting') return `${run.step} waits on you`;
  return run.round === null
    ? run.step
    : `${run.step}, round ${run.round.current} of ${run.round.max}`;
}

/** "$1.10", "—" without a cost. */
export function costLabel(cost: number | null | undefined): string {
  return cost === null || cost === undefined ? '—' : `$${cost.toFixed(2)}`;
}

/** "2 of 5 tasks done": one counter, the same in the plan card and on the project page. */
export function tasksDoneLabel(done: number, total: number): string {
  return `${done} of ${total} task${total === 1 ? '' : 's'} done`;
}

/**
 * "5 tasks · 2 running · $3.70 today": the project's tasks (runs of a workflow), as many as
 * its conversation has under it; their agent steps are not tasks, their cost counts.
 */
export function tasksSummary(tasks: readonly AssistantTask[]): string {
  // The sidebar's project row counts the same (H7).
  const running = tasks.filter((task) => workflowRunRuns(task.workflowRun)).length;
  const cost = tasks.reduce(
    (sum, task) =>
      sum +
      (task.workflowRun.costUsd ?? 0) +
      task.children.reduce((childSum, child) => childSum + (child.costUsd ?? 0), 0),
    0,
  );
  return [
    `${tasks.length} task${tasks.length === 1 ? '' : 's'}`,
    `${running} running`,
    ...(cost > 0 ? [`${costLabel(cost)} today`] : []),
  ].join(' · ');
}

/**
 * The automation a task runs, by its name when it started (else among `automations`, else its
 * id); none for a task an agent does alone.
 */
export function automationName(
  run: Pick<ConversationWorkflowRun, 'workflowId' | 'automationName'>,
  automations: readonly { id: string; name: string }[] = [],
): string | null {
  if (run.workflowId === null) return null;
  return (
    run.automationName ??
    automations.find((automation) => automation.id === run.workflowId)?.name ??
    run.workflowId
  );
}

/** A row of the plan card: an item, the task doing it, where it stands. */
export type PlanRow = {
  title: string;
  state: 'done' | 'running' | 'waiting' | 'failed' | 'next' | 'later';
  /** The conversation doing it, titled as the item (one name per task); null before it starts. */
  conversationId: string | null;
  /** "PR #556 · merged", "waits on you", "next", "later"… */
  status: string;
  /** The row's line on a phone: "Careful · Develop waits on you", "Quick · next". */
  line: string;
};

/** The plan card of a run that plans (Work as a team), only while it goes (§2.4). */
export type TeamPlan = {
  task: AssistantTask;
  /** "Work as a team · Mobile navigation". */
  title: string;
  /** Its automation ("Work as a team") and its goal ("Mobile navigation"). */
  workflow: string;
  goal: string;
  /** "wave 1 of 2 · 1 of 5 tasks done". */
  progress: string;
  rows: PlanRow[];
};

/** The plan a task's run reports, as its card shows it; null when it plans nothing or ended. */
export function teamPlanOf(
  task: AssistantTask | undefined,
  tasks: readonly AssistantTask[] = [],
  automations: readonly { id: string; name: string }[] = [],
): TeamPlan | null {
  const plan = task?.workflowRun.plan;
  if (task === undefined || plan == null) return null;
  if (task.workflowRun.state !== 'running' && task.workflowRun.state !== 'waiting') return null;
  const done = plan.items.filter((item) => item.state === 'done').length;
  const pr = (id: string | undefined) => {
    const found = tasks.find((entry) => entry.conversationId === id);
    return found?.pullRequest == null
      ? null
      : `PR #${found.pullRequest.number} · ${found.pullRequest.state}`;
  };
  const rows = plan.items.map((item): PlanRow => {
    const status =
      item.state === 'waiting'
        ? 'waits on you'
        : item.state === 'done'
          ? (pr(item.conversationId) ?? 'done')
          : item.state === 'running'
            ? (pr(item.conversationId) ?? 'running')
            : item.state;
    // On a phone the row says the automation and step of the task doing it, as the Tasks do.
    const run =
      item.state === 'done'
        ? undefined
        : tasks.find((entry) => entry.conversationId === item.conversationId);
    const line =
      run === undefined
        ? status
        : [
            automationName(run.workflowRun, automations),
            item.state === 'next' || item.state === 'later'
              ? item.state
              : taskStepLine(run, item.state),
          ]
            .filter((part) => part !== null)
            .join(' · ');
    return {
      title: item.title,
      state: item.state,
      conversationId: item.conversationId ?? null,
      status,
      line,
    };
  });
  const workflow = automationName(task.workflowRun, automations) ?? 'Agent';
  const goal = plan.goal || task.title;
  return {
    task,
    title: `${workflow} · ${goal}`,
    workflow,
    goal,
    progress: [
      ...(plan.wave === null ? [] : [`wave ${plan.wave.current} of ${plan.wave.total}`]),
      tasksDoneLabel(done, plan.items.length),
    ].join(' · '),
    rows,
  };
}
